import axios from 'axios';

/**
 * templateApi
 *
 * Plain, online-only wrappers around the Template CRUD + sharing endpoints.
 * Unlike case/juror data, templates are NOT cached in Dexie or queued for
 * offline sync — per the plan, this is "an occasional, online-only action."
 * Mirrors the axios usage already established in pages/CaseList/ShareModal.jsx
 * (axios.defaults.baseURL/withCredentials are configured once in
 * components/App/Root.jsx).
 */

/** List templates the current user owns or has been shared. */
export async function listTemplates() {
  const response = await axios.get('/api/templates');
  return response.data;
}

/** Create a new template (creator = current session user, set server-side). */
export async function createTemplate(templateData) {
  const response = await axios.post('/api/templates', templateData);
  return response.data;
}

/** Fetch a single template (requires view-or-edit access). */
export async function getTemplate(templateId) {
  const response = await axios.get(`/api/templates/${templateId}`);
  return response.data;
}

/** Full-replace update of a template's content (requires edit access). */
export async function updateTemplate(templateId, templateData) {
  const response = await axios.put(`/api/templates/${templateId}`, templateData);
  return response.data;
}

/** Delete a template (creator-only). */
export async function deleteTemplate(templateId) {
  const response = await axios.delete(`/api/templates/${templateId}`);
  return response.data;
}

/** List a template's shares (creator-only). */
export async function listTemplateShares(templateId) {
  const response = await axios.get(`/api/share/template/${templateId}`);
  return response.data.shares;
}

/** Share a template with another user by email (creator-only). */
export async function addTemplateShare(templateId, { email, accessLevel }) {
  const response = await axios.post(`/api/share/template/${templateId}`, { email, accessLevel });
  return response.data;
}

/** Update an existing share's access level (creator-only). */
export async function updateTemplateShare(templateId, shareId, { accessLevel }) {
  const response = await axios.put(`/api/share/template/${templateId}/${shareId}`, { accessLevel });
  return response.data;
}

/** Remove a share (creator-only). */
export async function removeTemplateShare(templateId, shareId) {
  const response = await axios.delete(`/api/share/template/${templateId}/${shareId}`);
  return response.data;
}

// Applying a template to a case is intentionally NOT here: it needs to write
// the merged result into Dexie so the UI reflects it immediately, which this
// module's plain axios wrappers don't do. That's db/actions.applyTemplateToCase
// (-> SyncEngine.applyTemplateToCase) — always import it from there.

export default {
  listTemplates,
  createTemplate,
  getTemplate,
  updateTemplate,
  deleteTemplate,
  listTemplateShares,
  addTemplateShare,
  updateTemplateShare,
  removeTemplateShare,
};
